import { extractApiError } from 'utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  changePasswordRequest,
  closeUserAccountRequest,
  createNotificationRequest,
  editUserRequest,
  getDocsRequest,
  getMeRequest,
  getNotificationRequest,
  getPatientVisitsRequest,
  getUserRequest,
  getUsersRequest,
  logInRequest,
  LoginRequestType,
  restorePasswordRequest,
  signUpRequest,
  SignUpRequestType,
  verifyResetRequest,
} from 'api';
import { LsKeys, routes } from 'enums';
import { authActions } from 'state';
import { getDoctor } from '../doctorThunk/doctorThunk';

export const getUsers = createAsyncThunk(
  'users/get',
  async (query: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getUsersRequest(query);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

export const getUser = createAsyncThunk(
  'user/get',
  async (id: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getUserRequest(id);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

export const getDocs = createAsyncThunk(
  'user/get/docs',
  async (id: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getDocsRequest(id);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

/**
 * What an operator is told when the server refuses a status change.
 *
 * Same reasoning as `CLOSE_ACCOUNT_ERRORS` below: the API's own sentences are
 * addressed to the account holder, and keyed on `code` because that is the
 * stable contract and the prose is not.
 *
 * Until this existed the status dropdown had NO failure path at all —
 * `editUser.rejected` set `isLoading = false` and discarded the payload — so a
 * refused or dropped request left the dropdown reading the new status, in
 * colour, over an account the server had not changed. An administrator would
 * have believed a provider was banned while they went on taking visits. That is
 * the one failure this control must never have.
 */
export const STATUS_CHANGE_ERRORS: Record<string, string> = {
  // The three statuses the dropdown greys out. Reachable here only from a
  // client that is not this console, or if the greying were ever removed — but
  // the message has to be right either way, because it is also what an operator
  // sees if their console is a version behind the API.
  USER_STATUS_NOT_WRITABLE:
    'That status is not set from here. Review a pending provider with Approve ' +
    'or Reject, and close an account with Close account.',
  // Lifting a billing suspension by hand would put a provider back to work
  // still owing. The Finance page is the route, and it lifts the hold itself.
  PAYMENT_HOLD_NOT_CLEARED_BY_HAND:
    'This provider is suspended for unpaid dues. Settle or write off their ' +
    'invoice on the Finance page and the suspension lifts by itself.',
  ACCOUNT_ALREADY_DELETED:
    'This account is closed, so its status can no longer be changed.',
  // A provider row whose `doctor` record is missing. Rare, and the database now
  // refuses to create one — but an account broken before that constraint
  // shipped would still land here, and "something went wrong" would send an
  // operator hunting in the wrong place.
  PROVIDER_PROFILE_MISSING:
    'This provider account is missing its profile record, so it cannot be ' +
    'changed. Report it — it needs a fix in the database, not in the console.',
  FORBIDDEN: 'You do not have permission to change this account.',
  NOT_FOUND: 'This account no longer exists. Refresh the page.',
};

export const editUser = createAsyncThunk(
  'user/patch',
  async ({ data, id }: any, { rejectWithValue }) => {
    try {
      const res = await editUserRequest(id, data);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, STATUS_CHANGE_ERRORS));
    }
  },
);

export const getPatientVisits = createAsyncThunk(
  'visits/get',
  async (query: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getPatientVisitsRequest(query);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

/**
 * What an operator is told when the server refuses to close an account.
 *
 * The API's own sentences are written for the ACCOUNT HOLDER -- "You still have
 * unpaid dues. Please settle them before deleting your account." -- because
 * until now the only caller was the person themselves, on their phone. Shown to
 * an administrator they read as an accusation about the administrator's own
 * account and, worse, name no action the console can take.
 *
 * Keyed on `code` rather than on the message text, for the reason
 * `extractApiError` gives: `code` is a contract
 * (Backend src/core/errors/error-codes.ts) and the prose is not.
 *
 * Every refusal `deleteAccount` can produce is here. None of them is a bug, and
 * none of them can be overridden from the console -- an administrator runs the
 * same gates the account holder does -- so each message says what has to change
 * before the closure will go through.
 */
export const CLOSE_ACCOUNT_ERRORS: Record<string, string> = {
  // The provider owes money. Refusing is what stops deletion being a way to
  // walk away from an invoice, so the only way forward is for the invoice to be
  // settled -- which the Finance PAGE can do (record a payment, or write the
  // dues off), while the Finance TAB on this profile only displays it. Naming
  // the wrong one sends the operator to a screen with no button on it.
  //
  // No figure is quoted. The API puts `totalDueCents` and `invoiceCount` in the
  // exception's context and the exception filter never serialises context to
  // the client, so a number in this sentence could only be invented.
  ACCOUNT_HAS_OUTSTANDING_DUES:
    'This provider still owes money, so the account cannot be closed yet. ' +
    'Settle or write off their unpaid invoices on the Finance page, then ' +
    'try again.',
  // Somebody is mid-visit. Refused rather than cancelled: on the other end is a
  // patient with a provider on the way.
  ACCOUNT_DELETION_VISIT_IN_PROGRESS:
    'There is a visit in progress on this account. Wait for it to finish, or ' +
    'have it cancelled, before closing the account.',
  ACCOUNT_ALREADY_DELETED:
    'This account is already closed. See Closed accounts for the record.',
  // Both the subject-is-an-admin rail in `deleteAccount` and a rejected guard
  // answer FORBIDDEN. Only the first is reachable from this console, where
  // every session is already an administrator.
  FORBIDDEN:
    'Administrator accounts cannot be closed here. Remove them on the Admins ' +
    'page, which also refuses to remove you or the last administrator.',
  NOT_FOUND: 'This account no longer exists. Refresh the page.',
};

/**
 * Closes a patient's or provider's account, as an administrator.
 *
 * Re-reads the account on success rather than patching the store, for the
 * reason `adminThunk`'s mutations give: the server owns what the row looks like
 * afterwards. Here it owns rather a lot of it -- the name becomes "Deleted
 * user", the phone becomes a tombstone, the documents are gone -- and guessing
 * any of that locally would show the operator a profile that never existed.
 *
 * `getUser` and `doctorThunk`'s `getDoctor` share the `user/get` type prefix, so
 * this one dispatch refreshes the patient page and the provider page alike.
 */
export const closeUserAccount = createAsyncThunk(
  'user/account/delete',
  async (
    { id, reason }: { id: string; reason?: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await closeUserAccountRequest(id, reason);

      await dispatch(getUser(id));

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, CLOSE_ACCOUNT_ERRORS));
    }
  },
);

export const userThunk = {
  getUsers,
  getUser,
  editUser,
  getPatientVisits,
  getDocs,
  closeUserAccount,
};
